import { EmailRounded, PhoneRounded, RateReviewRounded, SmsRounded } from "@mui/icons-material";
import { Box, IconButton, Link, List, ListItem, SxProps, Tooltip } from "@mui/material";
import { useContext } from "react";
import { useMessageDialogHandlers } from "../../../../App/State/Hooks/MessageDialog/useMessageDialog";
import { ISelectedCandidate } from "../../../../App/State/Slices/MessageDialogSlice";
import { IMessageContainers } from "../../../../MessageComponents/Enums/MessageEnums";
import { CandidateContext } from "../../../CandidateProfile";
import { CandidateCurrentTabContext } from "../../CandidateTabs/State/CandidateTabsState";
import { useFeaturePermission } from "../../../../App/State/Hooks/abacHooks";
import { AppFeatureLookup } from "../../../../App/Constants/AbacConstants";
import { crmLiteUpgradeMessage } from "../../../../App/Constants/CrmLiteConstants";

export default function ProfileQuickActions({ sx }: { sx?: SxProps }) {
  const { candidate } = useContext(CandidateContext);
  const { handleTabChange } = useContext(CandidateCurrentTabContext);
  const { handleOpenDialog } = useMessageDialogHandlers();

  const emailDirectAccess = useFeaturePermission(AppFeatureLookup.ProfileEmailCandidateAction);
  const textDirectAccess = useFeaturePermission(AppFeatureLookup.ProfileTextCandidateAction);
  const callDirectAccess = useFeaturePermission(AppFeatureLookup.ProfileCallCandidateAction);

  const candidatePhone = candidate.phoneNumbers?.find((phoneNumber) => phoneNumber.isValid);
  const candidateEmail = candidate.emailAddresses?.find(
    (email) => (email.validationAttempt && email.isValid) || !email.validationAttempt,
  );
  const hasPhone = candidatePhone !== undefined;
  const hasEmail = candidateEmail !== undefined;
  const candidateEntities = {
    [candidate.id]: {
      candidateId: candidate.id,
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      hasEmail,
      hasPhone,
      unsubscribed: candidate.unsubscribed,
      emailAddress: hasEmail ? candidateEmail?.emailAddress : undefined,
      phoneNumber: hasPhone ? candidatePhone?.phoneNumber : undefined,
    } as ISelectedCandidate,
  };

  const candidateIds = [candidate.id];

  const emailDirectMessaging = emailDirectAccess
    ? hasEmail
      ? "Email candidate directly."
      : "Candidate does not have an email address"
    : crmLiteUpgradeMessage;
  const callDirectMessaging = callDirectAccess
    ? hasPhone
      ? `Call ${candidate.firstName}`
      : "Candidate does not have a valid phone number"
    : crmLiteUpgradeMessage;
  const textDirectMessaging = textDirectAccess
    ? hasPhone
      ? "Send text message directly to candidate."
      : "Candidate does not have a valid phone number"
    : crmLiteUpgradeMessage;

  const handleOpenSendTextMessageIcon = () => {
    if (!textDirectAccess || !hasPhone) {
      return;
    }
    handleOpenDialog({
      isCandidateTextOpen: true,
      containerType: IMessageContainers.CandidateTextNow,
      candidateEntities,
      candidateIds,
    });
  };
  const handleOpenSendEmailMessageIcon = () => {
    if (!emailDirectAccess || !hasEmail) {
      return;
    }
    handleOpenDialog({
      isCandidateEmailOpen: true,
      containerType: IMessageContainers.CandidateEmailNow,
      candidateEntities,
      candidateIds,
    });
  };

  return (
    <Box sx={sx}>
      <List
        aria-label="Candidate Quick Actions"
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "1ch",
          padding: 0,
          "& .MuiListItem-root": {
            width: "auto",
            padding: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            fontSize: "0.875rem",
          },
          "& .MuiIconButton-root": {
            border: "2px solid rgba(0,0,0,0.3)",
          },
        }}
      >
        <Tooltip title={`View notes for ${candidate.firstName}`}>
          <ListItem>
            <IconButton
              aria-label="View or Write Candidate Notes"
              onClick={() => handleTabChange("Notes")}
              data-test="candidate-profile-actions-notes"
            >
              <RateReviewRounded sx={{ fontSize: "1rem" }} />
            </IconButton>
            <span aria-hidden="true">Note</span>
          </ListItem>
        </Tooltip>

        <Tooltip title={emailDirectMessaging}>
          <ListItem>
            <IconButton
              aria-label="Email Candidate Directly"
              disabled={!emailDirectAccess || !hasEmail}
              onClick={emailDirectAccess ? handleOpenSendEmailMessageIcon : undefined}
              data-test="candidate-profile-actions-email"
            >
              <EmailRounded sx={{ fontSize: "1rem" }} />
            </IconButton>
            <span>Email</span>
          </ListItem>
        </Tooltip>

        <Tooltip title={callDirectMessaging}>
          <ListItem>
            <IconButton
              component={Link}
              aria-label={`Call ${candidate.firstName}`}
              href={callDirectAccess && hasPhone ? `tel:${candidatePhone.phoneNumber}` : undefined}
              disabled={!callDirectAccess || !hasPhone}
              data-test="candidate-profile-actions-call"
            >
              <PhoneRounded sx={{ fontSize: "1rem" }} />
            </IconButton>
            <span>Call</span>
          </ListItem>
        </Tooltip>

        <Tooltip title={textDirectMessaging}>
          <ListItem>
            <IconButton
              aria-label="Text Candidate Directly"
              disabled={!textDirectAccess || !hasPhone}
              onClick={textDirectAccess ? handleOpenSendTextMessageIcon : undefined}
              data-test={`candidate-profile-actions-text`}
            >
              <SmsRounded sx={{ fontSize: "1rem" }} />
            </IconButton>
            <span>Text</span>
          </ListItem>
        </Tooltip>
      </List>
    </Box>
  );
}
